import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";

const API_BASE = "https://nissi-india.com/api/hireVehicleReport";

const toInt = (v, d = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : d;
};
const toStr = (v) => String(v ?? "").trim();

export default function HiredVehicleReportAllPrint() {
  const navigate = useNavigate();
  const { search } = useLocation();
  const qs = useMemo(() => new URLSearchParams(search), [search]);

  const [loading, setLoading] = useState(false);
  const [vehicleData, setVehicleData] = useState([]);

  const page = toInt(qs.get("page"), 1);
  const pageSize = toInt(qs.get("pageSize"), 15);

  // map backend row -> print row fields (adjust keys if your proc returns different names)
  const mapRow = (r, idx) => ({
    sNo: (page - 1) * pageSize + idx + 1,
    vehicleCode: r.fld_vehicle_code ?? r.vehicleCode ?? "",
    vehicleName: r.fld_vehicle_name ?? r.vehicleName ?? "",
    vehicleRegNo: r.fld_vehicle_regno ?? r.vehicleNumber ?? r.vehicleRegNo ?? "",
    vehicleSubTypeName: r.fld_vehicle_subtypename ?? r.vehicleType ?? r.vehicleSubTypeName ?? "",
    storeName: r.fld_branchname ?? r.storeName ?? "",
    hiredFrom: r.fld_hiredfrom ?? r.hiredFrom ?? "",
  });

  const fetchPrintData = async () => {
    try {
      setLoading(true);

      const params = Object.fromEntries(qs.entries()); // all query params
      const res = await axios.get(`${API_BASE}/list`, { params });

      if (!res.data?.success) throw new Error(res.data?.message || "Failed");

      const rows = (res.data.rows || []).map(mapRow);
      setVehicleData(rows);
    } catch (e) {
      setVehicleData([]);
      alert(e.message || "Failed to load print data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPrintData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const handlePrint = () => window.print();

  return (
    <div className="min-h-screen bg-white p-4">
      {/* Buttons hidden in print */}
      <div className="print:hidden mb-6 text-center flex gap-3 justify-center">
        <button
          onClick={() => navigate(-1)}
          className="bg-gray-200 text-black px-6 py-3 text-sm font-medium hover:bg-gray-300"
        >
          Back
        </button>
        <button
          onClick={handlePrint}
          className="bg-black text-white px-6 py-3 text-sm font-medium hover:bg-gray-800 transition-colors"
          disabled={loading}
        >
          {loading ? "Loading..." : "Print Report"}
        </button>
      </div>

      <div className="max-w-6xl mx-auto border-2 border-black p-4 print:max-w-none print:mx-0 print:border-2">
        <div className="border border-black p-3">
          {/* Header */}
          <div className="border border-black p-3 mb-4 flex items-center gap-5">
            <div className="w-28 h-20 bg-black text-white flex flex-col justify-center items-center text-xs font-bold">
              <div>NISSI</div>
              <div>INFOTECH</div>
              <div className="text-xs">PVT LTD</div>
            </div>
            <div className="flex-1 text-center">
              <h1 className="text-lg font-bold mb-2">Nissi Infotech</h1>
              <div className="text-xs leading-tight">
                <div>Nissi</div>
                <div className="font-semibold">CIVIL CONSTRUCTION</div>
                <div>3/42-12, Nagappa colony , kavundampalayam ,Tamil Nadu, India .</div>
                <div className="mt-1">
                  Phone No :9003744644 &nbsp;&nbsp;&nbsp;&nbsp; infraconsoft-implementation@nissiinfotech.in
                </div>
              </div>
            </div>
          </div>

          <div className="text-center text-base font-bold my-4">
            HIRED VEHICLE REPORT 
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full border-collapse border border-black text-xs">
              <thead>
                <tr className="bg-white">
                  <th className="border border-black p-2 text-center font-bold">S.No</th>
                  <th className="border border-black p-2 text-center font-bold">Vehicle Code</th>
                  <th className="border border-black p-2 text-center font-bold">Vehicle Name</th>
                  <th className="border border-black p-2 text-center font-bold">Vehicle Reg.No</th>
                  <th className="border border-black p-2 text-center font-bold">Vehicle SubType Name</th>
                  <th className="border border-black p-2 text-center font-bold">Store Name</th>
                  <th className="border border-black p-2 text-center font-bold">Hired From</th>
                </tr>
              </thead>
              <tbody>
                {vehicleData.map((item) => (
                  <tr key={item.sNo}>
                    <td className="border border-black p-2 text-center">{item.sNo}</td>
                    <td className="border border-black p-2 text-center">{item.vehicleCode}</td>
                    <td className="border border-black p-2 text-center">{item.vehicleName}</td>
                    <td className="border border-black p-2 text-center">{item.vehicleRegNo}</td>
                    <td className="border border-black p-2 text-center">{item.vehicleSubTypeName}</td>
                    <td className="border border-black p-2 text-center">{item.storeName}</td>
                    <td className="border border-black p-2 text-center">{item.hiredFrom}</td>
                  </tr>
                ))}

                {vehicleData.length === 0 && !loading && (
                  <tr>
                    <td className="border border-black p-3 text-center" colSpan={7}>
                      No records
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="mt-4 p-2 border border-black bg-gray-100 text-xs">
            Report generated by ERP-Software - www.infraconsoft.com owned by www.nissiinfotech.com
          </div>
        </div>
      </div>

      <style jsx>{`
        @media print {
          body { margin: 0; padding: 0; }
          * { -webkit-print-color-adjust: exact !important; color-adjust: exact !important; print-color-adjust: exact !important; }
          .print\\:hidden { display: none !important; }
          .print\\:max-w-none { max-width: none !important; }
          .print\\:mx-0 { margin-left: 0 !important; margin-right: 0 !important; }
          .print\\:border-2 { border-width: 2px !important; }
          table { page-break-inside: auto; }
          tr { page-break-inside: avoid; page-break-after: auto; }
        }
      `}</style>
    </div>
  );
}
